<script setup lang="ts">
  import { computed, onBeforeUnmount, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { SimpleLoading } from '@/shared/ui';
  import {
    clearBreadcrumbTrail,
    DocumentRevisionStatus,
    setBreadcrumbTrail,
    useCustomToast,
    type Breadcrumb,
  } from '@/shared/lib';
  import { RecordFormViewer } from '@/widgets/record-form-viewer';
  import type { FormStructure } from '@/features/FormBuilder';
  import { fetchHomologationRecord, type HomologationRecordView } from '../../api';

  const route = useRoute();
  const router = useRouter();
  const { showErrorToast } = useCustomToast();

  const record = ref<HomologationRecordView | null>(null);
  const isLoading = ref(true);
  const hasError = ref(false);
  let latestRequest = 0;

  const recordId = computed(() => Number(route.params.id));

  const viewerStructure = computed<FormStructure | null>(() => {
    const structure = record.value?.matrixStructure;
    if (!structure?.elements) return null;
    return {
      elements: structure.elements,
      version: structure.version,
      metadata: structure.metadata,
    };
  });

  /**
   * O Registro se pendura na etapa em que esta: quem o abre em Aguardando AP veio da Aprovacao, e em
   * Aguardando VG nao ha mais etapa, so a Homologacao. O ultimo nivel e o numero do Registro e nao e
   * link — ja se esta nele.
   */
  const STAGE_BREADCRUMB: Record<string, { name: string; path: string }> = {
    [DocumentRevisionStatus.PENDING_APPROVAL]: {
      name: 'Etapa de Análise Crítica e Aprovação',
      path: '/aprovacao',
    },
    [DocumentRevisionStatus.PENDING_VERIFICATION]: {
      name: 'Etapa de Verificação e Vigência',
      path: '/verificacao',
    },
    [DocumentRevisionStatus.VERIFIED]: { name: 'Homologação', path: '/homologacao' },
  };

  const buildTrail = (loaded: HomologationRecordView): Breadcrumb[] => {
    const stage = STAGE_BREADCRUMB[loaded.status] ?? {
      name: 'Homologação',
      path: '/homologacao',
    };

    return [
      { name: stage.name, path: stage.path },
      { name: loaded.prefix, path: route.fullPath, clickable: false },
    ];
  };

  async function loadRecord(): Promise<void> {
    // So a resposta mais nova pode escrever. Uma resposta lenta do Registro que o usuario acabou de
    // deixar cairia sobre o que esta em tela, formulario e trilha incluidos.
    const request = ++latestRequest;

    if (!Number.isFinite(recordId.value)) {
      // Descartar tambem aqui: vindo de um Registro valido para um id quebrado, deixar o anterior
      // em pe o desenharia sob a URL invalida, porque o galho de erro so vem depois dele.
      record.value = null;
      hasError.value = true;
      isLoading.value = false;
      showErrorToast({ title: 'Registro de homologação inválido.', type: 'error' });
      await router.push('/homologacao?type=REGISTRATION');
      return;
    }

    isLoading.value = true;
    hasError.value = false;

    try {
      const loaded = await fetchHomologationRecord(recordId.value);
      if (request !== latestRequest) return;
      record.value = loaded;
      setBreadcrumbTrail(buildTrail(loaded));
    } catch {
      if (request !== latestRequest) return;
      // Descartar importa: o redirecionamento nao e aguardado, e deixar o Registro anterior em pe
      // o desenharia, com a trilha dele, sob a URL que acabou de falhar.
      record.value = null;
      hasError.value = true;
      showErrorToast({
        title: 'Não foi possível carregar o registro da homologação.',
        type: 'error',
      });
      await router.push('/homologacao?type=REGISTRATION');
    } finally {
      if (request === latestRequest) {
        isLoading.value = false;
      }
    }
  }

  // O router mantem este componente vivo quando so o id muda, entao montar nao basta: sem o watch
  // imediato a tela continuaria desenhando o Registro com que foi aberta.
  watch(recordId, loadRecord, { immediate: true });

  onBeforeUnmount(() => {
    clearBreadcrumbTrail();
  });
</script>

<template>
  <div
    class="flex h-full flex-col"
    data-testid="homologation-record-page"
  >
    <div
      v-if="isLoading"
      class="flex flex-1 items-center justify-center"
      data-testid="homologation-record-loading"
    >
      <SimpleLoading />
    </div>

    <!-- Chaveado pelo Registro para que um novo receba um visualizador limpo: a etapa carrega o
         formulario na store ao montar, e o router mantem esta pagina viva quando so o id muda. -->
    <RecordFormViewer
      v-else-if="record && viewerStructure"
      :key="recordId"
      :structure="viewerStructure"
      :rules-structure="record.rulesStructure"
    />

    <div
      v-else-if="hasError"
      class="flex flex-1 items-center justify-center text-sm text-[var(--color-gray-03)]"
    >
      Este registro não está mais disponível na homologação.
    </div>
  </div>
</template>
